import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    Typography,
    CardContent,
    Avatar,
    CircularProgress,
    Grid,
    Divider,
    Button,
    Chip,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { getCustomerById } from "../services/customers";
import { getCustomerSessionDetailsByCustomerId } from "../services/customerSessionDetails";
import { getCustomerSessionDetails } from "../services/customerSessionDetails";
import { getCustomerFeedback } from "../services/customerFeedbackService";
import { getPostSessionScores } from "../services/customerAssessment";
import { getAssessmentsByAccountId } from "../services/assessments";
import { getAssessmentCountsByCustomer, getLatestHADSScoresByCustomer } from "../services/customerAssessment";
import { getDoctorsByAccountId } from "../services/doctors";
import { assessmentCountKey } from "../utils/assessmentKeys";
import LanguageToggle from "./LanguageToggle";
import { getEffectiveAccountIdNumber } from "../utils/accountId";
import { useProviderLanguage } from "../i18n/ProviderLanguageContext";


export default function AnalyticsPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useProviderLanguage();

    const { customerId, accountId: locationAccountId } = location.state || {};
    const [customer, setCustomer] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [loadingCustomer, setLoadingCustomer] = useState(false);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [errorCustomer, setErrorCustomer] = useState(null);
    const [expandedCycleId, setExpandedCycleId] = useState(null);
    const [sessionDetails, setSessionDetails] = useState({});
    const [assessments, setAssessments] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [postSessionScores, setPostSessionScores] = useState([]);
    const [hadsScore, setHadsScore] = useState(null);
    const [assessmentCounts, setAssessmentCounts] = useState({});
    const [hospitalName, setHospitalName] = useState("");





    const [accountId, setAccountId] = useState(null);
    const [effectiveAccountId, setEffectiveAccountId] = useState(null);

    useEffect(() => {
        const storedAccountId = localStorage.getItem("account_id");
        const finalAccountId = storedAccountId !== null ? storedAccountId : locationAccountId;

        if (finalAccountId !== null && finalAccountId !== undefined) {
            setAccountId(Number(finalAccountId)); // convert to number
            setEffectiveAccountId(getEffectiveAccountIdNumber(finalAccountId));
        }
    }, [locationAccountId]);

    // fetch the assessments assigned to this account (drives the dynamic labels below)
    useEffect(() => {
        if (!accountId) return;
        const diagnosis = customer?.primary_diagnosis?.toLowerCase();
        const disease = diagnosis === "cancer" || diagnosis === "kidney" ? diagnosis : undefined;
        const fetchAssessments = async () => {
            try {
                const data = await getAssessmentsByAccountId(accountId, disease);
                setAssessments(data || []);
            } catch (err) {
                console.error("Error fetching assessments:", err);
            }
        };
        fetchAssessments();
    }, [accountId, customer?.primary_diagnosis]);

    // fetch hospital display name (same source as Patients.jsx)
    useEffect(() => {
        const fetchHospitalName = async () => {
            try {
                const { displayname } = await getDoctorsByAccountId(accountId);
                setHospitalName(displayname || "");
            } catch (err) {
                console.error("Failed to load hospital name:", err);
            }
        };
        if (accountId) fetchHospitalName();
    }, [accountId]);

    // fetch latest HADS score + assessment counts for this patient
    useEffect(() => {
        if (!customerId) return;
        const fetchHadsAndCounts = async () => {
            try {
                const hadsData = await getLatestHADSScoresByCustomer(customerId);
                setHadsScore(hadsData);
            } catch (err) {
                console.error("Error fetching latest HADS score:", err);
            }
            try {
                const countsData = await getAssessmentCountsByCustomer(customerId);
                setAssessmentCounts(countsData || {});
            } catch (err) {
                console.error("Error fetching assessment counts:", err);
            }
        };
        fetchHadsAndCounts();
    }, [customerId]);



    //const match = assessments.find((a) => a.key === item.title);




    const moodRatingMap = {
        happy: "😊 Happy",
        sad: "😞 Sad",
        angry: "😡 Angry",
        disgust: "🤢 Disgust",
        surprised: "😲 Surprised",
        bad_low: "😔 Bad / Low",
        none: "❓ None of the above",
    };
    const feedbackMoodMap = {
        HAPPY: "😊 Happy",
        RELAXED: "😌 Relaxed",
        NEUTRAL: "😐 Neutral",
        SAD: "😟 Sad",
        VERY_SAD: "😢 Very Sad",


    };


    const sleepQualityMap = {
        very_poor: "😫 Very Poor",
        poor: "😴 Poor",
        fair: "😌 Fair",
        good: "😊 Good",
        excellent: "🌟 Excellent",
    };

    const languageMap = {
        eng: "English",
        hin: "Hindi",
        tel: "Telugu",
        ben: "Bengali",
        kan: "Kannada",
    };

    const StarDisplay = ({ value = 0, max = 5 }) => {
        if (!value) return <Typography>N/A</Typography>;

        return (
            <Box sx={{ display: "flex", gap: 0.5 }}>
                {[...Array(max)].map((_, index) => (
                    <Typography
                        key={index}
                        sx={{
                            fontSize: 20,
                            color: index < value ? "#facc15" : "#e5e7eb",
                        }}
                    >
                        ★
                    </Typography>
                ))}
            </Box>
        );
    };
    const painToStars = (pain) => {
        if (pain === null || pain === undefined) return 0;

        const numericPain = Number(pain);
        if (Number.isNaN(numericPain)) return 0;

        return Math.round((numericPain / 10) * 5);
    };

    const fields = sessionDetails
        ? [
            { label: "Treatment Cycle", value: sessionDetails.treatment_cycle },
            {
                label: "Blood Pressure",
                value: sessionDetails.blood_pressure
                    ? `${sessionDetails.blood_pressure} mmHg`
                    : "-",
            },
            {
                label: "Heart Rate",
                value: sessionDetails.heart_rate
                    ? `${sessionDetails.heart_rate} bpm`
                    : "-",
            },
            {
                label: "Pain Scale",
                value: sessionDetails.pain_scale
                    ? `${sessionDetails.pain_scale}/10`
                    : "-",
            },
            {
                label: "Mood Rating",
                value: moodRatingMap[sessionDetails.mood_rating] || "-",
            },
            { label: "Nausea", value: sessionDetails.nausea || "-" },
            { label: "Fatigue", value: sessionDetails.fatigue || "-" },
            { label: "Anxiety", value: sessionDetails.anxiety || "-" },
            { label: "Depression", value: sessionDetails.depression || "-" },
            { label: "Appetite Loss", value: sessionDetails.appetite_loss || "-" },
            {
                label: "Sleep Quality",
                value: sleepQualityMap[sessionDetails.sleep_quality] || "-",
            },
            {
                label: "Suggested Care Soundscape",
                value: sessionDetails.track_name || "-",
            },
            {
                label: "Assessment Type",
                value: Array.isArray(sessionDetails.assessment_type)
                    ? sessionDetails.assessment_type
                        .map((item) => {
                            const match = assessments.find((a) => a.key === item.title);
                            const title = match ? match.title : item.title;

                            if (title === "HADS Assessment" && sessionDetails.hads_score) {
                                return `${title} - ${sessionDetails.hads_score}`;
                            }

                            if (title === "EORTC QLQ-C30" && sessionDetails.eortc_score) {
                                return `${title} - ${sessionDetails.eortc_score}`;
                            }
                            if (title === "PSQI Assessment" && sessionDetails.psqi_score) {
                                return `${title} - ${sessionDetails.psqi_score}`;
                            }

                            return title;
                        })
                        .join(", ")
                    : "-",
            },


        ]
        : [];




    // Fetch Customer Details
    useEffect(() => {
        if (!customerId || !accountId) return;

        const fetchCustomer = async () => {
            try {
                setLoadingCustomer(true);
                setErrorCustomer(null);
                const data = await getCustomerById(customerId, accountId);
                setCustomer(data);
            } catch (err) {
                setErrorCustomer("Failed to load customer data.");
            } finally {
                setLoadingCustomer(false);
            }
        };

        fetchCustomer();
    }, [customerId, accountId]);

    // Fetch Treatment Sessions
    useEffect(() => {
        if (!customerId) return;

        const fetchSessions = async () => {
            try {
                setLoadingSessions(true);
                const data = await getCustomerSessionDetailsByCustomerId(customerId);
                // The API returns null when the patient has no sessions.
                setSessions(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error(err);
                setSessions([]);
            } finally {
                setLoadingSessions(false);
            }
        };

        fetchSessions();
    }, [customerId]);

    // HADS fallback from sessions
    useEffect(() => {
        const hasHads = (v) => v !== null && v !== undefined && v !== "";
        if (hasHads(hadsScore?.anxiety_score) || hasHads(hadsScore?.depression_score)) return;
        if (!sessions.length) return;

        const findLatestHADS = async () => {
            for (const session of sessions) {
                try {
                    const scores = await getPostSessionScores(session.id);
                    const hads = (scores || []).find(
                        (s) =>
                            String(s.assessment_type || "").toLowerCase().includes("hads") &&
                            (hasHads(s.anxiety_score) || hasHads(s.depression_score))
                    );
                    if (hads) {
                        setHadsScore({ anxiety_score: hads.anxiety_score, depression_score: hads.depression_score });
                        return;
                    }
                    const details = await getCustomerSessionDetails(session.id);
                    if (hasHads(details?.hads_anxiety) || hasHads(details?.hads_depression)) {
                        setHadsScore({ anxiety_score: details.hads_anxiety, depression_score: details.hads_depression });
                        return;
                    }
                } catch (err) {
                    console.error("HADS fallback failed:", err);
                }
            }
        };
        findLatestHADS();
    }, [sessions, hadsScore]);



    if (!customerId) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error" variant="h6">
                    No customer selected. Please go back and select a customer.
                </Typography>
                <Box mt={2}>
                    <Typography
                        sx={{ color: "#1976d2", cursor: "pointer" }}
                        onClick={() => navigate(-1)}
                    >
                        Go Back
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (loadingCustomer || loadingSessions) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    background: "#f8f9ff",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }
    // type → key
    const scoreKey = (type = "") =>
        ["hads", "psqi", "eortc", "kdqol"].find((k) => String(type).toLowerCase().includes(k));

   
    function mapSessionDetailsToUI(session) {
        return {
            id: session.id,
            treatment_cycle: session.treatment_cycle,
            assessment_type: session.assessment_type,
            preSession: {
                bloodPressure: session.blood_pressure ? `${session.blood_pressure} mmHg` : "N/A",
                heartRate: session.heart_rate ? `${session.heart_rate} bpm` : "N/A",
                painScale: session.pain_scale ? `${session.pain_scale}/10` : "N/A",
                moodRating: session.mood_rating || "N/A",
                symptoms: {
                    nausea: session.nausea || "N/A",
                    fatigue: session.fatigue || "N/A",
                    anxiety: session.anxiety || "N/A",
                    depression: session.depression || "N/A",
                    appetiteLoss: session.appetite_loss || "N/A",
                    sleepQuality: session.sleep_quality || "N/A",
                },
            },
            postSession: {
                hads_score: session.hads_score || "N/A",
                psqi_score: session.psqi_score || "N/A",
                eortc_score: session.eortc_score || "N/A",
                assessments: [
                    { name: "HADS Assessment", description: "Hospital Anxiety & Depression Scale", score: session.hads_score },
                    { name: "PSQI Sleep Quality", description: "Pittsburgh Sleep Quality Index", score: session.psqi_score },
                    { name: "EORTC QLQ-C30", description: "Quality of Life Assessment", score: session.eortc_score },
                ],
                post_blood_pressure: session.post_blood_pressure || "N/A",
                post_heart_rate: session.post_heart_rate || "N/A",
                post_pain_scale: session.post_pain_scale || "N/A",
                hads_anxiety: session.anxiety_score || "N/A",
                hads_depression: session.depression_score || "N/A",
            },
        };
    }


    // Add this function inside your component before return:

    const handleCycleClick = async (id) => {
        if (expandedCycleId === id) {
            setExpandedCycleId(null);
            setSessionDetails({});
            setFeedback(null);
            setPostSessionScores([]);
        } else {
            setExpandedCycleId(id);
            try {
                // First, fetch session details - this is the primary data source
                const sessionData = await getCustomerSessionDetails(id);

                // Map the session data to UI format immediately
                const mappedSession = mapSessionDetailsToUI(sessionData);
                setSessionDetails(mappedSession);

                // Then fetch feedback and assessment scores separately
                // These are optional and shouldn't affect session data display if they fail
                try {
                    const feedbackData = await getCustomerFeedback(id);
                    setFeedback(feedbackData);
                } catch (feedbackError) {
                    console.log("No feedback data available:", feedbackError);
                    setFeedback(null);
                }

                              try {
                    const assessmentScores = await getPostSessionScores(id);
                    // allowed only
                    setPostSessionScores(
                        (assessmentScores || []).filter((s) =>
                            assignedAssessmentKeys.has(scoreKey(s.assessment_type)))
                    );
                } catch (assessmentError) {
                    console.log("No assessment scores available:", assessmentError);
                    setPostSessionScores([]);
                }

            } catch (error) {
                console.error("Failed to fetch session details:", error);
                setSessionDetails({});
                setFeedback(null);
                setPostSessionScores([]);
            }
        }
    };


    const getHadsScores = () => {
        const hads = postSessionScores.find(
            (a) => a.assessment_type?.toLowerCase().includes("hads")
        );

        if (!hads) return { anxiety: "N/A", depression: "N/A", total: "N/A" };

        const anxiety = Number(hads.anxiety_score);
        const depression = Number(hads.depression_score);

        return {
            anxiety: hads.anxiety_score,
            depression: hads.depression_score,
            total: !isNaN(anxiety) && !isNaN(depression) ? anxiety + depression : "N/A",
        };
    };

    // KDQOL-36 has five component scores (workbook "Score Summary" sheet), never a single total.
    const KDQOL_COMPONENTS = [
        { key: "sf12_physical", label: "SF-12 Physical", column: "sf12_physical_score", answeredColumn: "sf12_answered", total: 12 },
        { key: "sf12_mental", label: "SF-12 Mental", column: "sf12_mental_score", answeredColumn: "sf12_answered", total: 12 },
        { key: "burden", label: "Burden", column: "burden_score", answeredColumn: "burden_answered", total: 4 },
        { key: "symptoms", label: "Symptoms", column: "symptoms_score", answeredColumn: "symptoms_answered", total: 12 },
        { key: "effects", label: "Effects", column: "effects_score", answeredColumn: "effects_answered", total: 8 },
    ];

    const formatComponentScore = (score) =>
        score === null || score === undefined || score === "" ? "N/A" : Number(score).toFixed(1);

    const getKdqolComponents = () => {
        const kdqol = postSessionScores.find((a) =>
            a.assessment_type?.toLowerCase().includes("kdqol")
        );
        if (!kdqol) return null;

        let summary = [];
        if (kdqol.score_summary) {
            try {
                const parsed = JSON.parse(kdqol.score_summary);
                if (Array.isArray(parsed)) summary = parsed;
            } catch (err) {
                console.error("Failed to parse KDQOL score summary:", err);
            }
        }

        return KDQOL_COMPONENTS.map((component) => {
            const stored = summary.find((s) => s.key === component.key);
            return {
                label: component.label,
                score: stored ? stored.score : kdqol[component.column],
                answered: stored ? stored.answered ?? 0 : kdqol[component.answeredColumn] ?? 0,
                total: stored ? stored.total ?? component.total : component.total,
            };
        });
    };

    // EORTC has no single total, so its 15 domain scores come from score_summary.
    const getEortcDomains = () => {
        const eortc = postSessionScores.find((a) =>
            a.assessment_type?.toLowerCase().includes("eortc")
        );
        if (!eortc?.score_summary) return null;

        try {
            const parsed = JSON.parse(eortc.score_summary);
            if (!Array.isArray(parsed) || parsed.length === 0) return null;
            return parsed.map((d) => ({
                label: d.label || d.key,
                score: d.score,
                answered: d.answered ?? 0,
                total: d.total ?? 0,
            }));
        } catch (err) {
            console.error("Failed to parse EORTC score summary:", err);
            return null;
        }
    };

    // The `score` column holds the raw item sum; the real 0-21 global score and
    // the seven components live in score_summary.
    const getPsqiSummary = () => {
        const psqi = postSessionScores.find((a) =>
            a.assessment_type?.toLowerCase().includes("psqi")
        );
        if (!psqi?.score_summary) return null;

        try {
            const parsed = JSON.parse(psqi.score_summary);
            if (!Array.isArray(parsed) || parsed.length === 0) return null;
            const global = parsed.find((c) => c.key === "global");
            return {
                global: global?.score ?? null,
                status: global?.status,
                components: parsed
                    .filter((c) => c.key !== "global")
                    .map((c) => ({ label: c.label || c.key, score: c.score, answered: c.score === null ? 0 : 1, total: 1, max: c.max ?? 3 })),
            };
        } catch (err) {
            console.error("Failed to parse PSQI score summary:", err);
            return null;
        }
    };

    // Assessments are assigned per session, not per account, so read the list off
    // the session itself rather than the account-wide assignment.
    const sessionAssignedKeys = (() => {
        const raw = sessionDetails?.assessment_type;
        let list = raw;
        if (typeof raw === "string") {
            try {
                list = JSON.parse(raw);
            } catch {
                list = [];
            }
        }
        const keys = new Set();
        (Array.isArray(list) ? list : []).forEach((a) => keys.add(assessmentCountKey(a?.title)));
        return keys;
    })();

    // the counts line lists only the assessments assigned to this patient's account + diagnosis
  const assignedAssessmentKeys = new Set(
    assessments.length > 0
        ? assessments.map((a) => assessmentCountKey(a.name))
        : effectiveAccountId === 1
            ? ["hads", "eortc"]
            : effectiveAccountId === 2
                ? ["hads", "psqi", "eortc"]
                : effectiveAccountId === 4 || effectiveAccountId === 5
                    ? ["hads", "psqi", "eortc"]
                    : ["hads", "psqi", "kdqol"]
);

// A session is complete once its assigned music has been played through
// (status 'completed'); 'active' and 'inprogress' both count as incomplete.
const sessionStatusChip = (session) =>
    session?.status?.toLowerCase() === "completed"
        ? { label: "Complete", bgcolor: "#dcfce7", color: "#166534" }
        : { label: "Incomplete", bgcolor: "#fee2e2", color: "#991b1b" };



    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 }, background: "#f8f9ff", minHeight: "100vh" }}>
            <Card
                sx={{
                    width: "100%",
                    p: { xs: 1.5, sm: 3 },
                    borderRadius: { xs: "8px", sm: "10px" },
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                }}
            >
                <CardContent sx={{ p: 0 }}>
                    {/* Subscriber Info */}

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: { xs: 1.5, sm: 2 },
                            flexWrap: { xs: 'wrap', sm: 'nowrap' },
                            gap: { xs: 1, sm: 0 }
                        }}
                    >
                        <Typography
                            variant="h6"
                            fontWeight={400}
                            sx={{ mb: 2, color: "#1f2937", fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
                        >
                            {t("subscriberDetails")}
                        </Typography>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <LanguageToggle sx={{ height: { xs: 36, sm: "auto" } }} />
                        <Button
                            variant="contained"
                            onClick={() => navigate("/patients")}
                            sx={{
                                bgcolor: "#4A90E2",
                                "&:hover": { bgcolor: "#357ABD" },
                                borderRadius: 2,
                                px: { xs: 2, sm: 2.5 },
                                py: { xs: 0.8, sm: 1 },
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: { xs: "0.85rem", sm: "0.875rem" },
                                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                                whiteSpace: "nowrap",
                                minWidth: "auto",
                                height: { xs: 36, sm: "auto" },
                            }}
                        >
                            {t("back")}
                        </Button>
                        </Box>
                    </Box>


                    <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, gap: { xs: 2, sm: 3 }, flexWrap: "wrap", mb: 2 }}>
                        {/* Profile Pic */}
                        <Avatar
                            src={customer?.avatar || "/man.png"}
                            alt={customer?.gender || "gender"}
                            sx={{ width: { xs: 72, sm: 100 }, height: { xs: 72, sm: 100 }, border: "2px solid #e0e7ff" }}
                        />

                        {/* Customer Details */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>
                                {customer?.firstname || "N/A"} {customer?.lastname || ""}
                                <span style={{ fontWeight: 400, marginLeft: 8 }}>| Age: {customer?.age || "N/A"}</span>
                            </Typography>
                            <Typography sx={{ mt: 0.5, fontSize: { xs: "0.85rem", sm: "1rem" }, wordBreak: "break-word" }}>
                                Email: {customer?.email || "N/A"} | Phone: {customer?.phone || "N/A"} | Preferred Language: {languageMap[customer?.preferred_language] || customer?.preferred_language || "N/A"}
                            </Typography>
                            <Typography sx={{ mt: 0.5, fontSize: { xs: "0.85rem", sm: "1rem" }, wordBreak: "break-word" }}>
                                Assigned Doctor: {customer?.assigned_doctor_name || "N/A"} | Diagnosis: {customer?.primary_diagnosis || "N/A"}
                            </Typography>
                            <Typography sx={{ mt: 0.5, fontSize: { xs: "0.85rem", sm: "1rem" } }}>
                                Created At: {customer?.created_at ? new Date(customer.created_at).toLocaleDateString() : "N/A"}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Medical Info */}
                    <Box sx={{ mt: 1, p: { xs: 1.5, sm: 2 }, borderRadius: "10px", backgroundColor: "#f9fafb", border: "1px solid #e5e7eb" }}>
                        <Typography sx={{ color: "#5c5c5cff", fontSize: { xs: "0.85rem", sm: "1rem" }, mt: 1 }}>
                            {t("medicalInformation")} </Typography>
                        <Typography sx={{ color: "#666", fontSize: { xs: "0.85rem", sm: "1rem" }, mt: 2 }}>
                            Primary Diagnosis: {customer?.primary_diagnosis || "N/A"} | Date of Diagnosis:{" "}
                            {customer?.date_of_diagnosis ? new Date(customer.date_of_diagnosis).toLocaleDateString() : "N/A"} | Treatment Start Date:{" "}
                            {customer?.treatment_start_date ? new Date(customer.treatment_start_date).toLocaleDateString() : "N/A"} | Hospital : {hospitalName || "N/A"}
                        </Typography>
                    </Box>

                    {/* Latest HADS Score + Number of Assessments Administered */}
                    <Box sx={{ mt: 1, p: { xs: 1.5, sm: 2 }, borderRadius: "10px", backgroundColor: "#f9fafb", border: "1px solid #e5e7eb" }}>
                        <Typography sx={{ color: "#666", fontSize: { xs: "0.85rem", sm: "1rem" }, fontWeight: 600 }}>
                            Latest HADS Score — Anxiety: {hadsScore?.anxiety_score ?? "N/A"} | Depression: {hadsScore?.depression_score ?? "N/A"}
                        </Typography>
                        <Typography sx={{ color: "#666", fontSize: { xs: "0.85rem", sm: "1rem" }, fontWeight: 600, mt: 1 }}>
                            Number of Assessments Administered — {Object.entries(assessmentCounts)
                                .filter(([key]) => assignedAssessmentKeys.has(key.toLowerCase()))
                                .map(([key, count]) => `${key.toUpperCase()}: ${String(count).padStart(2, "0")}`)
                                .join(" | ") || "N/A"}
                        </Typography>
                    </Box>

                    {/* Treatment History */}
                    {sessions?.length > 0 ? (
                        <Box sx={{ mt: 2, p: { xs: 1.5, sm: 2 }, borderRadius: "10px", backgroundColor: "#f9fafb", border: "1px solid #e5e7eb" }}>
                            <Typography
                                variant="h6"
                                fontWeight={400}
                                sx={{ color: "#1f2937", fontSize: { xs: "1.1rem", sm: "1.25rem" }, mb: 2 }}
                            >
                                {t("treatmentHistory")}
                            </Typography>

                            {sessions.map((item) => {
                                const expanded = expandedCycleId === item.id;

                                return (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            mb: 1,
                                            p: 2,

                                            borderRadius: 2,
                                            boxShadow: expanded ? 3 : 1,
                                            cursor: "pointer",
                                            background: expanded ? "#f9fafb" : "white",
                                            border: "1px solid",
                                            borderColor: expanded ? "#90caf9" : "#e0e0e0",
                                            transition: "all 0.3s ease",
                                        }}
                                        onClick={() => handleCycleClick(item.id)}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                Treatment Cycle - {item.treatment_cycle}
                                            </Typography>
                                            <Chip
                                                label={sessionStatusChip(item).label}
                                                size="small"
                                                sx={{
                                                    bgcolor: sessionStatusChip(item).bgcolor,
                                                    color: sessionStatusChip(item).color,
                                                    fontWeight: 600,
                                                }}
                                            />
                                        </Box>

                                        {expanded && (
                                            <>
                                                <Divider sx={{ my: 1 }} />

                                                <Grid
                                                    container
                                                    spacing={3}
                                                    sx={{
                                                        display: "flex",

                                                        alignItems: "stretch",
                                                    }}
                                                >
                                                    {/* Pre-Session */}

                                                    <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, width: "100%" }}>

                                                        <Card
                                                            sx={{
                                                                p: { xs: 2, sm: 3 },
                                                                borderRadius: 3,
                                                                background: "#f9fafb",
                                                                border: "1px solid #e5e7eb",
                                                                height: "100%",
                                                                width: { xs: "100%", md: "50%" },
                                                            }}
                                                        >
                                                            <Typography fontWeight={700} fontSize="1.1rem" color="#0a3a67" mb={2}>
                                                                {t("preSessionDetails")}
                                                            </Typography>

                                                            {/* Vitals */}
                                                            <Box
                                                                sx={{
                                                                    display: "grid",
                                                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                                                    gap: 1.5,
                                                                }}
                                                            >
                                                                <Typography><strong>{t("bloodPressure")}:</strong> {sessionDetails?.preSession?.bloodPressure || "N/A"}</Typography>
                                                                <Typography><strong>{t("heartRate")}:</strong> {sessionDetails?.preSession?.heartRate || "N/A"}</Typography>
                                                                <Typography><strong>{t("painScale")}:</strong> {sessionDetails?.preSession?.painScale || "N/A"}</Typography>
                                                                <Typography><strong>{t("moodRating")}:</strong> {moodRatingMap[sessionDetails?.preSession?.moodRating] || "N/A"}</Typography>
                                                                <Typography><strong>{t("sleepQuality")}:</strong> {sleepQualityMap[sessionDetails?.preSession?.symptoms?.sleepQuality] || "N/A"}</Typography>
                                                            </Box>

                                                            {/* Symptoms */}
                                                            <Typography mt={3} fontWeight={600} color="#0a3a67">
                                                                {t("commonSymptoms")}
                                                            </Typography>

                                                            <Box
                                                                sx={{
                                                                    mt: 1,
                                                                    display: "grid",
                                                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                                                    gap: 1,
                                                                }}
                                                            >
                                                                <Typography><strong>{t("nausea")}:</strong> {sessionDetails?.preSession?.symptoms?.nausea || "N/A"}</Typography>
                                                                <Typography><strong>{t("fatigue")}:</strong> {sessionDetails?.preSession?.symptoms?.fatigue || "N/A"}</Typography>
                                                                <Typography><strong>{t("anxiety")}:</strong> {sessionDetails?.preSession?.symptoms?.anxiety || "N/A"}</Typography>
                                                                <Typography><strong>{t("depression")}:</strong> {sessionDetails?.preSession?.symptoms?.depression || "N/A"}</Typography>
                                                                <Typography><strong>{t("appetiteLoss")}:</strong> {sessionDetails?.preSession?.symptoms?.appetiteLoss || "N/A"}</Typography>
                                                            </Box>
                                                        </Card>



                                                        {/* Post-Session */}

                                                        <Card
                                                            sx={{
                                                                p: { xs: 2, sm: 3 },
                                                                borderRadius: 3,
                                                                background: "#f9fafb",
                                                                border: "1px solid #e5e7eb",
                                                                height: "100%",
                                                                width: { xs: "100%", md: "50%" },
                                                            }}
                                                        >
                                                            <Typography
                                                                sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#0a3a67", mb: 2 }}
                                                            >
                                                                {t("postSessionDetails")}
                                                            </Typography>

                                                            <Box sx={{ mb: 2 }}>
                                                                <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "#0a3a67" }}>
                                                                    {t("assessmentScore")}
                                                                </Typography>

                                                                {(() => {
                                                                    const hads = sessionDetails?.postSession?.hads_score;
                                                                    const psqi = sessionDetails?.postSession?.psqi_score;
                                                                    const eortc = sessionDetails?.postSession?.eortc_score;
                                                                    const kdqolComponents = getKdqolComponents();
                                                                    const eortcDomains = getEortcDomains();
                                                                    const psqiSummary = getPsqiSummary();
                                                                    const { anxiety, depression } = getHadsScores();

                                                                    const has = {
                                                                        hads: (hads && hads !== "N/A") || (anxiety && anxiety !== "N/A"),
                                                                        psqi: !!psqiSummary || (psqi && psqi !== "N/A"),
                                                                        eortc: !!eortcDomains || (eortc && eortc !== "N/A"),
                                                                        kdqol: !!kdqolComponents,
                                                                    };

                                                                    // Fall back to whatever has data if the session lists no assessments.
                                                                    const showKey = (key) =>
                                                                        sessionAssignedKeys.size > 0 ? sessionAssignedKeys.has(key) : has[key];

                                                                    const visibleKeys = ["hads", "psqi", "eortc", "kdqol"].filter(showKey);

                                                                    if (visibleKeys.length === 0) {
                                                                        return (
                                                                            <Typography sx={{ mt: 1, fontSize: "1rem", color: "gray" }}>
                                                                                {t("noAssessmentsAssigned")}
                                                                            </Typography>
                                                                        );
                                                                    }

                                                                    const pending = (label) => (
                                                                        <Typography sx={{ color: "#98a2b3" }}>
                                                                            <strong>{label}:</strong> Not completed for this session
                                                                        </Typography>
                                                                    );

                                                                    const scoreGrid = (title, items) => (
                                                                        <Box sx={{ mt: 0.75 }}>
                                                                            <Typography sx={{ fontWeight: 700, color: "#0a3a67", mb: 0.75 }}>
                                                                                {title}
                                                                            </Typography>
                                                                            <Box
                                                                                sx={{
                                                                                    display: "grid",
                                                                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                                                                    gap: 0.75,
                                                                                }}
                                                                            >
                                                                                {items.map(({ label, score, answered, total, detail }) => (
                                                                                    <Box
                                                                                        key={label}
                                                                                        sx={{
                                                                                            p: 1,
                                                                                            border: "1px solid #dbeafe",
                                                                                            borderRadius: 2,
                                                                                            backgroundColor: "#fff",
                                                                                        }}
                                                                                    >
                                                                                        <Typography sx={{ fontSize: "0.85rem", color: "#475467", fontWeight: 600 }}>
                                                                                            {label}
                                                                                        </Typography>
                                                                                        <Typography sx={{ fontSize: "1rem", color: "#111827" }}>
                                                                                            <strong>{formatComponentScore(score)}</strong>{" "}
                                                                                            <span style={{ color: "#667085" }}>{detail ?? `(${answered}/${total})`}</span>
                                                                                        </Typography>
                                                                                    </Box>
                                                                                ))}
                                                                            </Box>
                                                                        </Box>
                                                                    );

                                                                    return (
                                                                        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                                                                            {showKey("hads") && (has.hads ? (
                                                                                <Typography>
                                                                                    <strong>HADS Score — :</strong> {hads}<strong> Anxiety:</strong> {anxiety}
                                                                                    {" | "}
                                                                                    <strong>{t("depression")}:</strong> {depression}
                                                                                </Typography>
                                                                            ) : pending("HADS"))}

                                                                            {showKey("psqi") && (has.psqi ? (
                                                                                psqiSummary ? (
                                                                                    <Box>
                                                                                        <Typography>
                                                                                            <strong>Global PSQI Score:</strong>{" "}
                                                                                            {psqiSummary.global ?? "N/A"}
                                                                                            {psqiSummary.global !== null && " / 21"}
                                                                                            {psqiSummary.global !== null && (
                                                                                                <span style={{ color: "#667085" }}>
                                                                                                    {" "}— {psqiSummary.global > 5 ? "Sleep difficulty suggested" : "No sleep difficulty suggested"}
                                                                                                </span>
                                                                                            )}
                                                                                        </Typography>
                                                                                        {scoreGrid("PSQI Component Scores", psqiSummary.components.map((c) => ({
                                                                                            label: c.label,
                                                                                            score: c.score,
                                                                                            detail: `out of ${c.max}`,
                                                                                        })))}
                                                                                    </Box>
                                                                                ) : (
                                                                                    <Typography>
                                                                                        <strong>Global PSQI Score:</strong> {psqi}
                                                                                    </Typography>
                                                                                )
                                                                            ) : pending("PSQI"))}

                                                                            {showKey("eortc") && (has.eortc ? (
                                                                                eortcDomains
                                                                                    ? scoreGrid("EORTC QLQ-C30 Domain Scores", eortcDomains)
                                                                                    : (
                                                                                        <Typography>
                                                                                            <strong>EORTC Score:</strong> {eortc}
                                                                                        </Typography>
                                                                                    )
                                                                            ) : pending("EORTC QLQ-C30"))}

                                                                            {showKey("kdqol") && (has.kdqol
                                                                                ? scoreGrid("KDQOL-36 Component Scores", kdqolComponents)
                                                                                : pending("KDQOL-36"))}
                                                                        </Box>
                                                                    );
                                                                })()}
                                                            </Box>

                                                            {/* POST VITAL + FEEDBACK */}
                                                            {/* POST VITAL + FEEDBACK */}
                                                            {/* <Box sx={{ mt: 3 }}> */}
                                                            <Box mt={3}>
                                                                <Grid container spacing={3} alignItems="flex-start">

                                                                    {/* -------- POST VITAL -------- */}
                                                                    <Grid item xs={12} md={6}>
                                                                        <Box sx={{ p: 0, borderRadius: 2 }}>
                                                                            <Typography fontWeight={600} mb={1} color="#0a3a67">
                                                                                {t("postVital")}
                                                                            </Typography>

                                                                            {(() => {
                                                                                const bp = sessionDetails?.postSession?.post_blood_pressure;
                                                                                const hr = sessionDetails?.postSession?.post_heart_rate;
                                                                                const pain = sessionDetails?.postSession?.post_pain_scale;

                                                                                const hasAny =
                                                                                    (bp && bp !== "N/A") ||
                                                                                    (hr && hr !== "N/A") ||
                                                                                    (pain && pain !== "N/A");

                                                                                return hasAny ? (
                                                                                    <Box
                                                                                        sx={{
                                                                                            mt: 1,
                                                                                            display: "grid",
                                                                                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                                                                            gap: 1.5,
                                                                                        }}
                                                                                    >
                                                                                        {bp && bp !== "N/A" && (
                                                                                            <Typography>
                                                                                                <strong>{t("bloodPressure")}:</strong> {bp} mmHg
                                                                                            </Typography>
                                                                                        )}
                                                                                        {hr && hr !== "N/A" && (
                                                                                            <Typography>
                                                                                                <strong>{t("heartRate")}:</strong> {hr} bpm
                                                                                            </Typography>
                                                                                        )}
                                                                                        {pain && pain !== "N/A" && (
                                                                                            <Typography>
                                                                                                <strong>{t("painScale")}:</strong> {pain}/10
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Box>
                                                                                ) : (
                                                                                    <Typography color="gray">
                                                                                        {t("postVitalUnavailable")}
                                                                                    </Typography>
                                                                                );
                                                                            })()}
                                                                        </Box>
                                                                    </Grid>

                                                                    {/* ---------------- FEEDBACK ---------------- */}
                                                                    <Grid item xs={12} md={6}>
                                                                        <Box sx={{ p: 0, borderRadius: 2 }}>
                                                                            <Typography fontWeight={600} mb={1} color="#0a3a67">
                                                                                {t("feedback")}
                                                                            </Typography>

                                                                            {!feedback ? (
                                                                                <Typography color="gray">
                                                                                    {t("noFeedback")}
                                                                                </Typography>
                                                                            ) : (
                                                                                <Box
                                                                                    sx={{
                                                                                        mt: 1,
                                                                                        display: "grid",
                                                                                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                                                                        gap: 1,
                                                                                    }}
                                                                                >
                                                                                    {/* ACCOUNT 1 */}
                                                                                    {effectiveAccountId === 1 && (
                                                                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                                                                                        <>
                                                                                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                                                                <Typography fontWeight={600}>Current Mood:</Typography>
                                                                                                <Typography>
                                                                                                    {feedbackMoodMap[feedback.mood] || feedback.mood || "N/A"}
                                                                                                </Typography>
                                                                                            </Box>

                                                                                            <Box sx={{ display: "flex", gap: 1, width: "100%", flexWrap: "wrap" ,  alignItems: "center" }}>
                                                                                                <Typography fontWeight={600}>Relaxation Level:</Typography>
                                                                                                <StarDisplay value={Number(feedback.relaxation_level)} />
                                                                                            </Box>

                                                                                            <Box sx={{ display: "flex", gap: 1, width: "100%" , flexWrap: "wrap" ,  alignItems: "center" }}>
                                                                                                <Typography fontWeight={600}>Music Level:</Typography>
                                                                                                <StarDisplay value={Number(feedback.music_rating)} />
                                                                                            </Box>

                                                                                            <Typography>
                                                                                                <strong>Comments:</strong>{" "}
                                                                                                {feedback.comments?.trim() || "No comments provided"}
                                                                                            </Typography>
                                                                                        </>
                                                                                        </Box>
                                                                                    )}

                                                                                    {/* ACCOUNT 2 */}
                                                                                   {(effectiveAccountId === 2 || effectiveAccountId === 3) && (
                                                                                        <>
                                                                                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                                                                <Typography fontWeight={600}><strong>Mood:</strong></Typography>
                                                                                                <Typography>
                                                                                                    {feedbackMoodMap[feedback.mood] || feedback.mood || "N/A"}
                                                                                                </Typography>
                                                                                            </Box>

                                                                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                                                                <Typography fontWeight={600}><strong>Pain Level:</strong></Typography>
                                                                                                <Typography>
                                                                                                    {feedback.pain_level !== null && feedback.pain_level !== undefined
                                                                                                        ? `${feedback.pain_level}/10`
                                                                                                        : "N/A"}
                                                                                                </Typography>
                                                                                            </Box>

                                                                                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                                                                <Typography fontWeight={600}><strong>Relaxation Level:</strong></Typography>
                                                                                                <Typography>{feedback.relaxation_level || "N/A"}</Typography>
                                                                                            </Box>

                                                                                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                                                                <Typography fontWeight={600}><strong>Session Effectiveness:</strong></Typography>
                                                                                                <Typography>{feedback.session_feedback || "N/A"}</Typography>
                                                                                            </Box>

                                                                                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                                                                <Typography fontWeight={600}><strong>Experience:</strong></Typography>
                                                                                                <Typography>
                                                                                                    {Array.isArray(feedback.experience)
                                                                                                        ? feedback.experience.join(", ")
                                                                                                        : "N/A"}
                                                                                                </Typography>
                                                                                            </Box>

                                                                                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                                                                <Typography fontWeight={600}><strong>Interested:</strong></Typography>
                                                                                                <Typography>{feedback.interested || "N/A"}</Typography>
                                                                                            </Box>

                                                                                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                                                                                <Typography fontWeight={600}><strong>Comments:</strong></Typography>
                                                                                                <Typography>
                                                                                                    {feedback.comments?.trim() || "No comments provided"}
                                                                                                </Typography>
                                                                                            </Box>
                                                                                        </>
                                                                                    )}
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                    </Grid>
                                                                </Grid>
                                                            </Box>




                                                        </Card>
                                                    </Box>
                                                    {/* </Grid> */}
                                                </Grid>

                                                <Divider sx={{ my: 4 }} />
                                            </>

                                        )}
                                    </Box>
                                );

                            })}
                        </Box>
                    ) : (
                        <Typography sx={{ mt: 2 }}>No treatment history found.</Typography>
                    )}

                </CardContent>
            </Card>
        </Box>
    );
}